import type { Approver } from '../../components/organisms/createTeamModal/peopleTable';
import type { GetCardSourceData } from '../../queries/paymentQuery';
import type { GetTeamsResponse } from '../../queries/teamQuery';
import type { ZustandStore } from '../store.type';
import type { CardFormData } from './cardSourceSlice';
import type { NamedSet } from 'zustand/middleware';

type BillingAddress = {
	addressLine1: string;
	city: string;
	countryCode: string;
	region: string;
	zipCode: string;
};

export type CreditCard = {
	teamsCount: string;
	billingCurrency: string;
	billingAddress: BillingAddress;
	currency: string;
	id: string;
	lastDigits: string | number;
	name: string;
	provider: string;
	status: string;
	label: string;
	providerLabel: string;
};

export type CardSourceInfo = {
	id: string;
	label: string;
	lastDigits: string | number;
	name: string;
	provider: string;
};

type PaymentState = {
	payment: {
		payment: CreditCard[];
		card: GetCardSourceData | null;
		enroll?: Record<string, unknown>;
		approvers?: Approver[];
		cards?: CreditCard[];
		teamCards?: CreditCard[];
		cardSourceTeams?: GetTeamsResponse[];
		cardSourceId?: string;
		enrollData?: CardFormData;
	};
};

type PaymentActions = {
	setPayment: (payment: PaymentState['payment']['payment']) => void;
	setCardSource: (cardSource: PaymentState['payment']['card']) => void;
	updateCardSource: (cardSource: CreditCard) => void;
	setEnrollAmex: (enroll: PaymentState['payment']['enroll']) => void;
	setTeamApprovers: (approvers: PaymentState['payment']['approvers']) => void;
	setCompanyCards: (cards: PaymentState['payment']['cards']) => void;
	setTeamCards: (teamCards: PaymentState['payment']['teamCards']) => void;
	setCardSourceTeams: (cardSourceTeams: PaymentState['payment']['cardSourceTeams']) => void;
	setCardSourceId: (cardSourceId: PaymentState['payment']['cardSourceId']) => void;
	updateCardSourceName: (name: string) => void;
	setEnrollCardData: (data: GetCardSourceData) => void;
};

export type PaymentSlice = PaymentState & PaymentActions;

const paymentInitialState: PaymentState = {
	payment: {
		payment: [],
		card: {} as GetCardSourceData,
		enroll: {},
		approvers: [],
		cards: [],
		teamCards: [],
		cardSourceTeams: [],
		cardSourceId: '',
		enrollData: {} as CardFormData,
	},
};

export function paymentSlice(set: NamedSet<ZustandStore>): PaymentSlice {
	return {
		...paymentInitialState,
		setPayment(payment: PaymentState['payment']['payment']): void {
			set(
				(state: PaymentState) => {
					state.payment.payment = payment;
					return state;
				},
				false,
				'[setPayment]',
			);
		},
		setCardSource(cardSource: PaymentState['payment']['card']): void {
			set(
				(state: PaymentState) => {
					state.payment.card = cardSource || state.payment.card;
					return state;
				},
				false,
				'[setCardSource]',
			);
		},
		updateCardSource(cardSource: CreditCard): void {
			set(
				(state: PaymentState) => {
					state.payment.payment = [...state.payment.payment, structuredClone(cardSource)];
					return state;
				},
				false,
				'[updateCardSource]',
			);
		},
		updateCardSourceName(name: string): void {
			set(
				(state: PaymentState) => {
					if (!state.payment.card) {
						return state;
					}
					state.payment.card.name = name;
					return state;
				},
				false,
				'[updateCardSourceName]',
			);
		},
		setEnrollAmex(enroll: PaymentState['payment']['enroll']): void {
			set(
				(state: PaymentState) => {
					state.payment.enroll = enroll || {};
					return state;
				},
				false,
				'[setEnrollAmex]',
			);
		},
		setTeamApprovers(approvers: PaymentState['payment']['approvers']): void {
			set(
				(state: PaymentState) => {
					state.payment.approvers = approvers || [];
					return state;
				},
				false,
				'[setTeamApprovers]',
			);
		},
		setCompanyCards(cards: PaymentState['payment']['cards']): void {
			set(
				(state: PaymentState) => {
					state.payment.cards = cards || [];
					return state;
				},
				false,
				'[setCompanyCards]',
			);
		},
		setTeamCards(teamCards: PaymentState['payment']['teamCards']): void {
			set(
				(state: PaymentState) => {
					state.payment.teamCards = teamCards || [];
					return state;
				},
				false,
				'[setTeamCards]',
			);
		},
		setCardSourceTeams(cardSourceTeams: PaymentState['payment']['cardSourceTeams']): void {
			set(
				(state: PaymentState) => {
					state.payment.cardSourceTeams = cardSourceTeams || [];
					return state;
				},
				false,
				'[setCardSourceTeams]',
			);
		},
		setCardSourceId(cardSourceId: PaymentState['payment']['cardSourceId']): void {
			set(
				(state: PaymentState) => {
					state.payment.cardSourceId = cardSourceId || '';
					return state;
				},
				false,
				'[setCardSourceId]',
			);
		},

		setEnrollCardData(data: GetCardSourceData): void {
			set(
				(state: PaymentState) => {
					state.payment.card = data;
					state.payment.payment = [...state.payment.payment, structuredClone(data as CreditCard)];
					return state;
				},
				false,
				'[setEnrollCardData]',
			);
		},
	};
}

function formatExpiryDate(month: number, year: number) {
	return year.toString() + month.toString().padStart(2, '0');
}

function buildBasePaymentInformation(formData: CardFormData) {
	return {
		address: {
			address: formData.address,
			city: formData.city,
			countryCode: formData.countryCode,
			regionCode: formData.regionCode,
			zipCode: formData.zipCode.toString(),
		},
		billingCurrency: formData.billingCurrency,
		cardNumber: formData.cardNumber.toString().replaceAll(/\s+/g, ''),
		email: formData.email,
		expiryDate: formatExpiryDate(Number(formData.expirationMonth), Number(formData.expirationYear)),
		firstName: formData.firstName,
		lastName: formData.lastName,
		name: formData.name,
		orgName: formData.orgName,
	};
}

export function buildAmexPaymentInformation(formData: CardFormData) {
	const baseInfo = buildBasePaymentInformation(formData);
	return {
		...baseInfo,
		address: {
			...baseInfo.address,
			countryCode: 'US',
		},
		provider: 'AMEX',
		networkProvider: 'AMEX',
	};
}

export function buildHsbcPaymentInformation(formData: CardFormData) {
	const baseInfo = buildBasePaymentInformation(formData);
	return {
		...baseInfo,
		address: {
			...baseInfo.address,
			countryCode: 'GB',
		},
		provider: 'HSBC',
		networkProvider: 'VISA',
	};
}

function buildBaseCardSource(formData: CardFormData, cardSourceInfo: CardSourceInfo) {
	return {
		billingAddress: {
			addressLine1: formData.address,
			city: formData.city,
			countryCode: formData.countryCode,
			region: formData.regionCode,
			zipCode: formData.zipCode,
		},
		id: cardSourceInfo.id,
		billingCurrency: formData.billingCurrency,
		teamsCount: '0',
		label: [cardSourceInfo.name + ',', '····', cardSourceInfo.lastDigits].join(' '),
		lastDigits: cardSourceInfo.lastDigits,
		name: cardSourceInfo.name,
		provider: cardSourceInfo.provider,
		status: 'IN_PROGRESS',
	};
}

export function buildAmexCardSource(formData: CardFormData, cardSourceInfo: CardSourceInfo) {
	const baseInfo = buildBaseCardSource(formData, cardSourceInfo);
	return {
		...baseInfo,
		providerLabel: 'American Express',
	};
}

export function buildHsbcCardSource(formData: CardFormData, cardSourceInfo: CardSourceInfo) {
	const baseInfo = buildBaseCardSource(formData, cardSourceInfo);
	return {
		...baseInfo,
		providerLabel: 'HSBC',
	};
}
